import { useEffect, useCallback, useRef } from "react";
import { Header, useAIConfirm } from "@components";
import {
  useDimLoading,
  useDimLoadComplete,
  useDebouncedCallback,
} from "@/hooks";
import { useAtom, atom } from "jotai";
import { aiConfirmState } from "./state.js";

// TODO: 광고 로직하고 AI하고 통합하기
const AI = () => {
  const [confirmState, setConfirmState] = useAtom(aiConfirmState);
  const confirm = useAIConfirm();
  const dimLoadComplete = useDimLoadComplete();
  const dimLoading = useDimLoading();
  const initializedLoading = useDebouncedCallback(() => {
    dimLoading();
  });
  const chatlWrapperRef = useRef(null);
  const wrapperRef = useRef(null);
  const initialized = useRef(false);
  const iframeLoadedRef = useRef(false);
  const [state, setState] = useAtom(aiAtomState);
  const itemKey = "goodmiyorning-pchecked";
  useKeyboard(wrapperRef);

  const setVh = () => {
    // 실제 보이는 영역 1vh
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  const setItemWithExpiry = () => {
    const now = Date.now();
    const item = {
      value: true,
      expiry: now + 7776000 * 1000, // 현재 시간 + 90일
    };
    localStorage.setItem(itemKey, JSON.stringify(item));
  };

  const getItemWithExpiry = () => {
    const itemStr = localStorage.getItem(itemKey);
    if (!itemStr) return null;

    try {
      const item = JSON.parse(itemStr);
      // 만료되었으면 삭제 후 null 반환
      if (Date.now() > item.expiry) {
        localStorage.removeItem(itemKey);
        return null;
      }
      return item.value;
    } catch {
      // JSON 파싱 오류나 형식이 다르면, 그냥 원래 값 반환
      return itemStr;
    }
  };

  const hasPrivacyItem = () => {
    return getItemWithExpiry() !== null;
  };
  const handleResize = useCallback(() => {
    setState((s) => ({
      ...s,
      viewportWidth: window.innerWidth,
    }));
    document.body.style.setProperty(
      "min-height",
      "calc((var(--vh) * 100))",
      "important"
    );
    document.body.style.setProperty(
      "height",
      "calc((var(--vh) * 100))",
      "important"
    );
  }, []);

  const callPrivacy = useCallback(
    () => setState((s) => ({ ...s, showPrivacyHtml: true })),
    [state.showPrivacyHtml]
  );
  const closePrivacyHtml = () => {
    setState((s) => ({ ...s, showPrivacyHtml: false }));
  };
  const goAI = useCallback(() => {
    // 로컬 스토리지 설정(90일)
    setItemWithExpiry();

    setState((s) => ({ ...s, showPrivacyHtml: false }));
    // 초기 로딩 상태 설정
    initializedLoading();

    // 전역 설정 객체
    window.chtlConfig = {
      chatbotId: "1718871939",
      display: "page_inline",
      variables: {
        user_id: crypto.randomUUID(),
      },
    };

    // script 엘리먼트 생성
    const script = document.createElement("script");
    script.id = "chtl-script";
    script.src = "https://chatling.ai/js/embed.js";
    script.async = true;
    script.type = "text/javascript";
    script.dataset.id = "1718871939";
    script.dataset.display = "page_inline";
    document.body.appendChild(script);
    // 스크립트 로드 예상 시간이 지난 후 scriptLoaded 플래그 값 true로 변경
    const timer = setTimeout(() => {
      setState((s) => ({ ...s, script, scriptLoaded: true }));
    }, 700);
    setState((s) => ({ ...s, timer }));

    const interval = setInterval(() => {
      // 3초 이내에 iframe이 로드 되지 않았다면, load fail 처리
      // UI에서 통신 실패 안내 후 리로딩 유도할 것
      if (!iframeLoadedRef?.current) {
        setState((s) => ({ ...s, failedLoaded: true }));
      }
      dimLoadComplete();
      clearInterval(interval);
    }, 5000);
    setState((s) => ({ ...s, interval }));
    setConfirmState((s) => ({ ...s, isOpen: false }));
  }, [iframeLoadedRef?.current]);
  const onConfirm = useCallback(() => {
    if (!confirmState.checked) {
      alert("개인정보처리방침에 동의해주세요.");
      return;
    }
    goAI();
  }, [confirmState.checked]);

  const onCancel = useCallback(() => {
    setConfirmState((s) => ({ ...s, isOpen: false }));
    window.location.href = "/";
  }, []);

  // 초기 상태 설정
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      setState((s) => ({ ...s, iframeLoadedRef }));
      if (hasPrivacyItem()) {
        let interval1 = setInterval(() => {
          if (interval1) {
            goAI();
            setState((s) => ({ ...s, interval1: null }));
            clearInterval(interval1);
          }
        }, 1800);
        setState((s) => ({ ...s, interval1 }));
      } else {
        fetch("/privacy_250722.html")
          .then((res) => res.text())
          .then((privacyHtml) => setState((s) => ({ ...s, privacyHtml })));
        let interval1 = setInterval(() => {
          if (interval1) {
            confirm();
            setState((s) => ({ ...s, interval1: null }));
            clearInterval(interval1);
          }
        }, 1800);
        setState((s) => ({ ...s, interval1 }));
      }
    }

    // 이벤트 리스너 등록
    window.addEventListener("resize", handleResize);
    // 초기 한번 실행
    handleResize();

    // 실제 높이 계산(브라우저 제외)
    window.addEventListener("resize", setVh);
    setVh();

    // 컴포넌트 언마운트 시 스크립트 제거 (클린업)
    return () => {
      if (state.script) {
        document.body.removeChild(state.script);
      }
      if (state.iframeObserver) {
        state.iframeObserver.disconnect();
      }
      if (state.timer) {
        clearTimeout(state.timer);
      }
      delete window.chtlConfig;
      window.removeEventListener("resize", handleResize);
      document.body.style.removeProperty("height");
      document.body.style.removeProperty("min-height");
      dimLoadComplete();
      clearInterval(state.interval);
      clearInterval(state.interval1);
    };
  }, []);

  // 쿠키 및 개인정보처리방침 팝업 상태 설정
  useEffect(() => {
    setConfirmState((s) => ({
      ...s,
      msg: "브라우저 저장소 이용 및\n 개인정보처리방침에 동의하시겠습니까? (필수)",
      onConfirm,
      onCancel,
      callPrivacy,
    }));
  }, [
    confirmState.msg,
    confirmState.checked,
    confirmState.onConfirm,
    confirmState.onCancel,
    confirmState.callPrivacy,
  ]);

  // MutationObserver 설정
  useEffect(() => {
    if (state.scriptLoaded) {
      // MutationObserver 설정
      const iframeObserver = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (node.tagName === "IFRAME" && node.id === "chtl-chat-iframe") {
              iframeLoadedRef.current = true;
              setState((s) => ({ ...s, iframeLoadedRef }));
              dimLoadComplete();
              // 조건 달성시 종료
              iframeObserver.disconnect();
            }
          });
        });
      });

      iframeObserver.observe(chatlWrapperRef.current, {
        childList: true,
        subtree: true,
      });
      setState((s) => ({ ...s, iframeObserver }));
    }
  }, [state.scriptLoaded]);

  return (
    <>
      {state.showPrivacyHtml && (
        <div>
          <div
            style={{
              backgroundColor: "transparent",
              position: "fixed",
              top: 0,
              left: state.viewportWidth >= 690 ? "calc(-345px + 50vw)" : 0,
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              maxWidth: "690px",
              width: "100vw",
              height: "80px",
              zIndex: 100002,
            }}
          >
            <div
              style={{
                backgroundColor: "transparent",
                boxSizing: "border-box",
                paddingRight: "32px",
                fontWeight: "bold",
                cursor: "pointer",
                maxWidth: "695px",
                width: "20px",
                height: "20px",
                alignSelf: "start",
                paddingTop: "5px",
                zIndex: 100002,
              }}
              onClick={closePrivacyHtml}
            >
              X
            </div>
          </div>
          <div
            style={{
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              padding: "16px",
              position: "fixed",
              maxWidth: "692px",
              width: "100vw",
              height: "100vh",
              overflowY: "auto",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 100001,
            }}
            dangerouslySetInnerHTML={{ __html: state.privacyHtml }}
          />
        </div>
      )}
      {state.scriptLoaded && (
        <div
          ref={wrapperRef}
          style={{
            backgroundColor: "#000000",
          }}
        >
          {!state.keyboardOn && (
            <div style={{ height: "80px", width: "100%" }}>
              <Header title="배변 훈련 AI" />
            </div>
          )}
          <div
            style={{
              maxWidth: "690px",
              width: "100vw",
              height: state.keyboardOn
                ? "calc(var(--vh) * 100)"
                : "calc((var(--vh) * 100) - 80px)",
              overflow: state.keyboardOn ? "visible" : "hidden",
              overflowY: state.keyboardOn ? "auto" : "none",
            }}
          >
            <div
              ref={chatlWrapperRef}
              id="chtl-inline-bot"
              style={{
                width: "100%",
                height: state.keyboardOn
                  ? "calc(var(--vh) * 100)"
                  : "calc((var(--vh) * 100) - 145px)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {state.failedLoaded && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "calc((var(--vh) * 100) - 145px)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      fontFamily: "Inter, var(--default-font-family)",
                      fontSize: "clamp(12px, 4vw ,20px)",
                      color: "white",
                      textAlign: "center",
                    }}
                  >
                    연결이 끊어졌습니다. 잠시 후에 다시 시도해주세요.
                  </div>
                </div>
              )}
              <div
                style={{
                  backgroundColor: "#000000",
                  position: "absolute",
                  bottom: 0,
                  height: "39px",
                  width: "100vw",
                  maxWidth: "690px",
                  zIndex: 99998,
                  color: "#ffffff",
                  fontFamily: "Inter, var(--default-font-family)",
                  fontSize: "12px",
                  fontWeight: "600",
                  padding: "12px 0 0 16px",
                  boxSizing: "border-box",
                  textAlign: "start",
                }}
              >
                * 배변 훈련에 대해 궁금한 점들을 물어보세요.
              </div>
            </div>
            <div
              style={{
                height: state.keyboardOn ? "0px" : "65px",
                width: "100vw",
                maxWidth: "690px",
                backgroundColor: "#ffffff",
                overflow: "hidden",
              }}
            >
              <AdEmbed />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default AI;

export const aiAtomState = atom({
  privacyHtml: "",
  showPrivacyHtml: false,
  interval: null,
  interval1: null,
  script: null,
  iframeObserver: null,
  scriptLoaded: false,
  iframeLoadedRef: null,
  failedLoaded: false,
  viewportWidth: null,
  keyboardOn: false,
  timer: null,
});

export function AdEmbed() {
  const [aiState, _] = useAtom(aiAtomState);
  const [state, setState] = useAtom(adEmbedAtomState);

  useEffect(() => {
    let interval = setInterval(() => {
      // 1분 후 푸쉬 배너 광고 시작
      // 전면 광고 해제
      // 오픈 스펙에서 전면 광고, 푸쉬 광고 제외
      // setState((s) => ({
      //   ...s,
      //   overTwoMinutes: true,
      //   showVignetteBanner: false,
      // }));
      clearInterval(interval);
    }, 1000 * 60 * 1);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        maxWidth: "690px",
        width: "100vw",
        height: "100%",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "stretch",
        overflow: "hidden",
      }}
    >
      {/* 오픈 스펙에서 전면 광고, 푸쉬 광고 제외 */}
      {/* {state.showVignetteBanner && (
        <iframe src="/vignette-banner.html" width="0" height="0" />
      )}
      {state.overTwoMinutes && (
        <iframe src="/in-page-push.html" width="0" height="0" />
      )} */}
      {aiState.viewportWidth >= 625 && wideCoupangBanner()}
      {aiState.viewportWidth < 625 && narrowCoupangBanner()}
    </div>
  );
}

const adEmbedAtomState = atom({
  overTwoMinutes: false,
  showVignetteBanner: true,
});

const wideCoupangBanner = () => {
  return (
    <div
      style={{
        position: "relative",
        maxWidth: "690px",
        width: "100vw",
        height: "100%",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          maxWidth: "690px",
          width: "100vw",
          height: "50%",
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          overflow: "hidden",
          boxSizing: "border-box",
          padding: "0 10px 0 10px",
        }}
      >
        <iframe
          src="https://coupa.ng/ci9PnV"
          width="100%"
          height="44"
          frameBorder="0"
          scrolling="no"
          referrerPolicy="unsafe-url"
          browsingtopics={true.toString()}
        ></iframe>
        <div style={{ width: "10px", height: "1px" }}></div>
        <iframe
          src="https://ads-partners.coupang.com/widgets.html?id=893964&template=banner&trackingCode=AF9156469&subId=&width=320&height=50"
          width="320"
          height="50"
          frameBorder="0"
          scrolling="no"
          referrerPolicy="unsafe-url"
          browsingtopics={true.toString()}
        ></iframe>
      </div>
      <div
        style={{
          height: "10px",
          lineHeight: "10px",
          fontSize: "8px",
          color: "darkgoldenrod",
          fontFamily: "Inter, var(--default-font-family)",
          fontWeight: "600",
        }}
      >
        * 이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를
        제공받습니다.
      </div>
    </div>
  );
};

const narrowCoupangBanner = () => {
  return (
    <div
      style={{
        maxWidth: "690px",
        width: "100vw",
        height: "100%",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <iframe
        src="https://ads-partners.coupang.com/widgets.html?id=893964&template=banner&trackingCode=AF9156469&subId=&width=320&height=50"
        width="320"
        height="50"
        frameBorder="0"
        scrolling="no"
        referrerPolicy="unsafe-url"
        browsingtopics={true.toString()}
      ></iframe>
      <div
        style={{
          height: "10px",
          lineHeight: "10px",
          fontSize: "8px",
          color: "darkgoldenrod",
          fontFamily: "Inter, var(--default-font-family)",
          fontWeight: "600",
        }}
      >
        * 이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를
        제공받습니다.
      </div>
    </div>
  );
};

// 모바일 환경에서 키보드로 인한 높이 변화를 감지한다.
// 높이 변화가 일어날 때, keyboardOn 상태 변경과 scrollToElementManually 함수를 실행한다.
// 원본 뷰포트 높이를 반환한다.
const useKeyboard = (wrapperRef) => {
  // 원본 뷰포트 높이
  const originalHeightRef = useRef(window.innerHeight);
  const [_, setAIState] = useAtom(aiAtomState);

  useEffect(() => {
    const onResize = () => {
      // visualViewport.height 지원 시엔 실제 보이는 영역
      const currentHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      const prevHeight = originalHeightRef.current;
      const keyboardHeight = prevHeight - currentHeight;

      // wrapper 요소 참조
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      // 키보드가 올라왔다고 판단될 만큼 뷰포트가 줄어들면
      if (keyboardHeight > 100) {
        setAIState((s) => ({ ...s, keyboardOn: true }));
      } else {
        setAIState((s) => ({ ...s, keyboardOn: false }));
      }
    };

    // 이벤트 등록
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onResize);
    } else {
      window.addEventListener("resize", onResize);
    }

    // 초기 실행
    onResize();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", onResize);
      } else {
        window.removeEventListener("resize", onResize);
      }
    };
  }, [wrapperRef]);
  return originalHeightRef.current;
};
